import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

/**
 * CONFIGURACIÓN PARA CREATE REACT APP (CRA)
 *
 * La configuración sale del entorno (.env) si está disponible; si no, se usan los valores
 * de PRODUCCIÓN de más abajo. Eso permite compilar en una máquina limpia, recién clonada y
 * sin .env, sin que el build salga silenciosamente vacío.
 *
 * Si recibís 'auth/invalid-api-key' teniendo un .env, es que 'process.env' no cargó:
 * 1. Detené la terminal (Ctrl + C).
 * 2. Ejecutá 'npm start' nuevamente (CRA lee el .env una sola vez, al arrancar).
 * 3. Verificá que el .env esté en la RAÍZ del proyecto (fuera de src).
 */

/**
 * Valores de producción. Son los mismos que estuvieron escritos acá hasta
 * "Backup Versión 2.0" (2026-05-25), cuando se movieron al .env.
 *
 * No son secretos: CRA los hornea en el bundle que se le sirve a cualquier visitante, así
 * que ya son públicos. Lo que protege al proyecto es firestore.rules y App Check, no
 * ocultar estos valores.
 */
const PROD_CONFIG = {
    apiKey: "AIzaSyACy8pWsNjtOYxjWYe8o6YTtDWDe65aoNE",
    authDomain: "base-de-cliente-ib.firebaseapp.com",
    projectId: "base-de-cliente-ib",
    storageBucket: "base-de-cliente-ib.appspot.com",
    messagingSenderId: "973638995277",
    appId: "1:973638995277:web:45263c391c9bb6def5fb04"
};
const PROD_RECAPTCHA_SITE_KEY = "6Lfe_P0sAAAAACQd50rZbenvxJZgPmmxt6NwtLlo";

const envConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

/**
 * Todo o nada, nunca una mezcla: si el entorno define aunque sea una variable, mandan las
 * del entorno y ninguna cae en el valor de producción.
 *
 * Un fallback campo por campo sería peor que no tener ninguno. Un .env del espejo al que le
 * falte una línea daría un híbrido —projectId del espejo con la apiKey de producción— que
 * no falla al compilar y se rompe en runtime de formas difíciles de leer.
 */
const missingFromEnv = Object.keys(envConfig).filter((key) => !envConfig[key]);
const usingEnvConfig = missingFromEnv.length < Object.keys(envConfig).length;

const firebaseConfig = usingEnvConfig ? envConfig : PROD_CONFIG;

// --- DEPURACIÓN DE SEGURIDAD ---
// Este log te dirá en la consola del navegador si las variables están llegando.
if (usingEnvConfig && missingFromEnv.length > 0) {
    console.error(
        "❌ ERROR CRÍTICO: el entorno define algunas variables de Firebase pero no todas. " +
        "Faltan: " + missingFromEnv.join(", ") + ". La app va a fallar; completá el .env."
    );
} else if (!usingEnvConfig) {
    console.warn("⚠️ Sin variables REACT_APP_ en el entorno: usando la configuración de PRODUCCIÓN embebida.");
}

// Inicializa Firebase
const app = initializeApp(firebaseConfig);

// --- CONFIGURACIÓN DE APP CHECK (SEGURIDAD DE TRÁFICO) ---
if (typeof window !== "undefined") {
    // Sigue el mismo criterio: si la config vino del entorno, la site key también.
    const siteKey = usingEnvConfig ? process.env.REACT_APP_RECAPTCHA_SITE_KEY : PROD_RECAPTCHA_SITE_KEY;

    if (siteKey) {
        console.log("🔒 [App Check] Inicializando sistema de seguridad reCAPTCHA...");

        // 1. Activar el token de depuración en entorno local (localhost) para no auto-bloquearte
        if (process.env.NODE_ENV !== "production") {
            window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
            console.log("🛠️ [App Check] Entorno de desarrollo detectado. Tu token de depuración aparecerá debajo:");
        }

        initializeAppCheck(app, {
            provider: new ReCaptchaEnterpriseProvider(siteKey),
            isTokenAutoRefreshEnabled: true
        });
    } else {
        console.warn("⚠️ [App Check] Inicialización omitida: 'REACT_APP_RECAPTCHA_SITE_KEY' no está definida en tu archivo .env.");
        console.info("💡 Si acabas de agregar la clave al archivo .env, recuerda detener tu terminal local (Ctrl + C) y ejecutar 'npm start' de nuevo.");
    }
}

// Exporta los servicios segurizados
export const auth = getAuth(app);
export const db = getFirestore(app);

export default app;
